// Color Storage and Persistence System

import { CustomColorScheme, ColorStorageData, ColorSchemeExport } from '../types/colorTypes';

const STORAGE_KEY = 'performanceTypeColors';
const STORAGE_VERSION = '1.0';

/**
 * Save custom colors to localStorage
 */
export const saveCustomColors = (colors: CustomColorScheme): void => {
  try {
    const storageData: ColorStorageData = {
      version: STORAGE_VERSION,
      lastModified: new Date().toISOString(),
      customColors: colors
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
  } catch (error) {
    console.error('Failed to save custom colors:', error);
  }
};

/**
 * Load custom colors from localStorage
 */
export const loadCustomColors = (): CustomColorScheme => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    
    const data: ColorStorageData = JSON.parse(stored);
    
    // Version compatibility check
    if (data.version !== STORAGE_VERSION) {
      console.warn('Color storage version mismatch, resetting to defaults');
      return {};
    }
    
    return data.customColors || {};
  } catch (error) {
    console.error('Failed to load custom colors:', error);
    return {};
  }
};

/**
 * Clear all custom colors from storage
 */
export const clearCustomColors = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear custom colors:', error);
  }
};

/**
 * Export color scheme to JSON
 */
export const exportColorScheme = (
  colors: CustomColorScheme, 
  name: string, 
  description?: string
): ColorSchemeExport => {
  return {
    name,
    description,
    createdAt: new Date().toISOString(),
    colors
  };
};

/**
 * Import color scheme from JSON
 */
export const importColorScheme = (schemeData: string): ColorSchemeExport | null => {
  try {
    const scheme: ColorSchemeExport = JSON.parse(schemeData);
    
    // Validate structure
    if (!scheme.name || !scheme.colors || !scheme.createdAt) {
      throw new Error('Invalid color scheme format');
    }
    
    return scheme;
  } catch (error) {
    console.error('Failed to import color scheme:', error);
    return null;
  }
};

/**
 * Generate downloadable JSON file for color scheme
 */
export const downloadColorScheme = (scheme: ColorSchemeExport): void => {
  try {
    const dataStr = JSON.stringify(scheme, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${scheme.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_colors.json`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download color scheme:', error);
  }
};

/**
 * Get storage usage information
 */
export const getStorageInfo = (): { used: number; total: number; percentage: number } => {
  try {
    let total = 0;
    let used = 0;
    
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        used += localStorage[key].length + key.length;
      }
    }
    
    // Estimate total storage (usually 5-10MB for localStorage)
    total = 5 * 1024 * 1024; // 5MB default estimate
    
    return {
      used,
      total,
      percentage: (used / total) * 100
    };
  } catch (error) {
    console.error('Failed to get storage info:', error);
    return { used: 0, total: 0, percentage: 0 };
  }
};